import { type DefaultError, useMutation, useQueryClient } from '@tanstack/react-query';
import { invalidateAssetDataForNodeChange } from '@/hooks/websocket/mergeAssetPayload';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { moveNodesQuery } from '@/services/tree.service';
import { type MoveNodesResponse } from '@/types/tree';
import { QueryKey } from '@/lib/queryKeys';

export const useMoveNodesMutation = () => {
  const queryClient = useQueryClient();
  const moveNodesQueryFn = useAuthenticatedQueryFn(moveNodesQuery);

  const invalidateMoveNodesQueries = () => {
    void queryClient.invalidateQueries({ queryKey: [QueryKey.Tree] });
    void invalidateAssetDataForNodeChange(queryClient);
  };

  type MoveNodesPayload = Parameters<typeof moveNodesQuery>['0'];
  type MoveNodesVariables = {
    move: MoveNodesPayload;
    suppressGlobalErrorToast?: boolean;
  };

  const { mutateAsync: moveNodesMutation } = useMutation<MoveNodesResponse, DefaultError, MoveNodesVariables>({
    mutationFn: (params) => moveNodesQueryFn(params.move),
    onSettled: invalidateMoveNodesQueries,
  });

  const moveNodes = (move: MoveNodesPayload, options?: { suppressGlobalErrorToast?: boolean }) =>
    moveNodesMutation({ move, suppressGlobalErrorToast: options?.suppressGlobalErrorToast });

  return {
    moveNodes,
    moveNodesMutation,
  };
};
